import { useState } from 'react';
import { invalidateChats, patchChatsConversation } from '../../../lib/chats';
import { groupChatAction, handleLostAccess } from '../../../lib/group-chat-api';
import { outcomeErrorKey } from '../../../lib/group-chat-actions';
import type { GroupParticipant, GroupSummary, MessageAuthor } from '../../../lib/group-chat';
import {
  APPROVAL_STATE_KEYS,
  approvalCardText,
  approvalCardView,
  maestroForAuthor,
  resolveOutcome,
  unavailableLine,
  type ApprovalLocal,
  type GroupApproval,
} from '../../../lib/group-maestro';
import { clearMaestroPrompt } from '../../../lib/maestro-prompt';
import { queryClient, queryKeys } from '../../../lib/query-client';
import { groupMembersKey } from '../../../lib/use-group-summary';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { Modal } from '../../Modal';
import { Badge, Button, Card, Typography } from '../ui';
import { GroupParticipantAvatar, groupStyles } from './GroupAvatars';
import { GROUP_AVATAR } from '../../../lib/group-chat';

/**
 * Symphony GROUP CHAT, Phase 5 — the members' own Maestros on web: the approval card, the "can't help right now"
 * notice, the gentle prompt and the add / remove confirms. The rules are `lib/group-maestro.ts`; iOS
 * `GroupMaestroViews.swift` and Android `GroupMaestroViews.kt` draw the same pieces.
 */

/** After a join or a removal: the sheet, the chat's summary and every chat list re-read the group. */
export function refreshGroup(conversationId: string) {
  void queryClient.invalidateQueries({ queryKey: groupMembersKey(conversationId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.conversation(conversationId) });
  patchChatsConversation(conversationId, {});
  invalidateChats();
}

/** `join-maestro` — the caller's OWN Maestro (nothing in the body names whose). Null on success, else the message. */
export async function joinMyMaestro(conversationId: string, t: (k: string) => string): Promise<string | null> {
  const outcome = await groupChatAction('join-maestro', { conversationId });
  if (outcome.kind === 'ok') {
    clearMaestroPrompt(conversationId);
    refreshGroup(conversationId);
    return null;
  }
  if (outcome.kind === 'refused' && handleLostAccess(conversationId, outcome.refusal)) return null;
  return t(outcomeErrorKey(outcome));
}

/** "Add {maestroAgentName} to this group?" → join. Used by the sheet's "Add my Maestro" row and by the prompt. */
export function AddMaestroDialog({ conversationId, agentName, onClose, onJoined }: { conversationId: string; agentName: string; onClose: () => void; onJoined?: () => void }) {
  const { t } = useTranslation();
  const { logModalActionBi } = useGenericBi();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const confirm = async () => {
    logModalActionBi({ page_name: 'chat', modal_name: 'group_add_maestro', action: 'add' } as never);
    setBusy(true);
    setError(null);
    const failed = await joinMyMaestro(conversationId, t);
    setBusy(false);
    if (failed) {
      setError(failed);
      return;
    }
    onJoined?.();
    onClose();
  };
  return (
    <Modal open onClose={onClose} width={440} maxHeightPx={650} scrollBody title={t('chat.group.maestro.addTitle', { maestroAgentName: agentName })}
      footer={
        <>
          <Button variant="outlined" onClick={() => { logModalActionBi({ page_name: 'chat', modal_name: 'group_add_maestro', action: 'cancel' } as never); onClose(); }} disabled={busy}>{t('chat.group.cancel')}</Button>
          <Button loading={busy} onClick={() => void confirm()} data-testid="group-maestro-add-confirm">{t('chat.group.maestro.addConfirm')}</Button>
        </>
      }>
      <Typography variant="p2" color="secondary" component="p">{t('chat.group.maestro.addBody', { maestroAgentName: agentName })}</Typography>
      {error && <Typography variant="p2" component="p" role="alert" style={{ color: 'var(--ds-text-destructive)', marginTop: 8 }}>{error}</Typography>}
    </Modal>
  );
}

/**
 * The gentle prompt above the composer after joining a group (while pending AND the viewer can add their Maestro):
 * Add → the same confirm + join; Not now → cleared for this conversation.
 */
export function MaestroPromptBanner({ conversationId, agentName, addName }: { conversationId: string; agentName: string; /** The confirm's name (`ownMaestroGroupName`); defaults to `agentName`. */ addName?: string }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const [confirming, setConfirming] = useState(false);
  return (
    <>
      <Card variant="sunken" size="sm" data-testid="group-maestro-prompt" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 10, flex: '1 1 220px', minWidth: 0 }}>
          <GroupParticipantAvatar participant={{ id: 'me', kind: 'MAESTRO', displayName: agentName, role: 'MEMBER' }} size={GROUP_AVATAR.message.size} />
          <Typography variant="p2" color="strong" component="span">{t('chat.group.maestro.prompt', { maestroAgentName: agentName })}</Typography>
        </span>
        <span style={{ display: 'flex', gap: 8, marginInlineStart: 'auto' }}>
          <Button size="small" variant="outlined" onClick={() => { logPageClickBi({ page_name: 'chat', button_name: 'group_maestro_prompt_not_now' }); clearMaestroPrompt(conversationId); }}>
            {t('chat.group.maestro.notNow')}
          </Button>
          <Button size="small" onClick={() => { logPageClickBi({ page_name: 'chat', button_name: 'group_maestro_prompt_add' }); setConfirming(true); }}>
            {t('chat.group.maestro.addConfirm')}
          </Button>
        </span>
      </Card>
      {confirming && <AddMaestroDialog conversationId={conversationId} agentName={addName ?? agentName} onClose={() => setConfirming(false)} />}
    </>
  );
}

/** A Maestro's "can't help right now" notice: a centred muted line, like a membership line. Never a number. */
export function MaestroUnavailableLine({ id, reason, summary, author }: { id?: string; reason: string; summary: GroupSummary; author?: MessageAuthor | null }) {
  const { t } = useTranslation();
  const maestro = maestroForAuthor(summary, author);
  return (
    <div data-msg-id={id} data-testid="group-maestro-unavailable" className={groupStyles.eventLine} style={{ display: 'flex', justifyContent: 'center' }}>
      {unavailableLine(reason, maestro?.displayName || t('home.map.maestro'), t)}
    </div>
  );
}

/**
 * The public approval card, in place of the Maestro's bubble (under its author line). Only the Maestro's owner sees
 * Approve / Deny; everyone else sees the state chip. One call at a time; a final answer hides the buttons.
 */
export function GroupApprovalCard({ card, summary, conversationId }: { card: GroupApproval; summary: GroupSummary; conversationId: string | null }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const [local, setLocal] = useState<ApprovalLocal | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const view = approvalCardView(card, summary.viewerId, Date.now(), local);
  const text = approvalCardText(card, summary, t);
  const answer = async (approve: boolean) => {
    if (!conversationId || view.busy) return;
    logPageClickBi({ page_name: 'chat', button_name: approve ? 'group_approval_approve' : 'group_approval_deny', conversation_id: conversationId } as never);
    setLocal((prev) => ({ ...(prev ?? {}), busy: true }));
    setMessage(null);
    const outcome = await groupChatAction('resolve-approval', { conversationId, approvalId: card.approvalId, approve });
    if (outcome.kind === 'refused' && handleLostAccess(conversationId, outcome.refusal)) return;
    const result = resolveOutcome(outcome);
    setLocal({ ...result.local, busy: false });
    setMessage(result.messageKey ? t(result.messageKey) : null);
  };
  return (
    <Card variant="outline" size="sm" data-testid="group-approval-card" style={{ maxWidth: 420, width: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 6 }}>
      <Typography variant="p1" color="strong" component="p" style={{ fontWeight: 'var(--sym-weight-semibold)' }}>{text.title}</Typography>
      {text.askedBy && <Typography variant="p3" color="secondary" component="p">{text.askedBy}</Typography>}
      <Typography variant="p3" color="secondary" component="p">{text.needs}</Typography>
      <div style={{ display: 'flex', gap: 8, marginTop: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        {view.showButtons ? (
          <>
            <Button size="small" disabled={view.busy} loading={view.busy} onClick={() => void answer(true)} data-testid="group-approval-approve">{t('chat.group.approval.approve')}</Button>
            <Button size="small" variant="outlined" disabled={view.busy} onClick={() => void answer(false)} data-testid="group-approval-deny">{t('chat.group.approval.deny')}</Button>
          </>
        ) : (
          <Badge variant={view.state === 'APPROVED' || view.state === 'EXECUTED' ? 'done' : view.state === 'PENDING' ? 'waiting-on-a-reply' : 'paused'}
            label={t(APPROVAL_STATE_KEYS[view.state])} data-testid="group-approval-state" />
        )}
      </div>
      {message && <Typography variant="p3" component="p" role="status" style={{ color: 'var(--ds-text-secondary)' }}>{message}</Typography>}
    </Card>
  );
}

/** "Remove {name}?" → `remove-maestro`, the Maestro named by its owner. */
export function RemoveMaestroDialog({ conversationId, maestro, label, onClose }: { conversationId: string; maestro: GroupParticipant; label: string; onClose: () => void }) {
  const { t } = useTranslation();
  const { logModalActionBi } = useGenericBi();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const confirm = async () => {
    if (!maestro.ownerId) return;
    logModalActionBi({ page_name: 'chat', modal_name: 'group_remove_maestro', action: 'remove' } as never);
    setBusy(true);
    setError(null);
    const outcome = await groupChatAction('remove-maestro', { conversationId, ownerMetaSiteId: maestro.ownerId });
    setBusy(false);
    if (outcome.kind === 'ok') {
      refreshGroup(conversationId);
      onClose();
      return;
    }
    if (outcome.kind === 'refused' && handleLostAccess(conversationId, outcome.refusal)) {
      onClose();
      return;
    }
    setError(t(outcomeErrorKey(outcome)));
  };
  return (
    <Modal open onClose={onClose} width={440} maxHeightPx={650} scrollBody title={t('chat.group.maestro.removeTitle', { name: label })}
      footer={
        <>
          <Button variant="outlined" onClick={onClose} disabled={busy}>{t('chat.group.cancel')}</Button>
          <Button color="destructive" loading={busy} onClick={() => void confirm()} data-testid="group-maestro-remove-confirm">{t('chat.group.maestro.removeConfirm')}</Button>
        </>
      }>
      <Typography variant="p2" color="secondary" component="p">{t('chat.group.maestro.removeBody')}</Typography>
      {error && <Typography variant="p2" component="p" role="alert" style={{ color: 'var(--ds-text-destructive)', marginTop: 8 }}>{error}</Typography>}
    </Modal>
  );
}
